import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, User, Search, Menu, ArrowLeft, LogIn, X, UserPlus, Mail, Loader, MessageCircle, Radio, MapPin, MapPinOff, Newspaper, MessageSquare, Users, Trophy, Target, Building, ChevronRight, UserCheck, GraduationCap, Home, Calendar, CalendarDays, Image, Zap } from 'lucide-react';
import { useLocation as useRouteLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useStreamChat } from '../../messaging/contexts/StreamChatContext';
import { useLocation as useUserLocation } from '../../contexts/LocationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { usePoll } from '../../contexts/PollContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { notificationService } from '../../notification/notificationService';
import { setNativeBadge, clearAndroidNotifications } from '../../notification/nativePushService';
import { useProfileImage } from '../../profile/hooks/useProfileImage';
import { searchAPI } from '../../services/api';
import { invitationsApi } from '../../invitations/services/invitationsApi';
import { apiCall } from '../../services/apiUtil';
import Logo from './Logo';
import SearchResults from './SearchResults';
import GuestMenuDropdown from './GuestMenuDropdown';
import LiveEventsList, { getLiveEventLink } from './LiveEventsList';
import { btn } from '../../utils/buttonStyles';
import { safeAreaTop } from '../../utils/helpers';

const ACADEMY_NAV_TABS = [
  { id: 'overview',  label: 'Overview',        icon: Home },
  { id: 'feeds',     label: 'Feeds',           icon: MessageSquare },
  { id: 'faculty',   label: 'Faculty',         icon: Users },
  { id: 'programs',  label: 'Programs',         icon: Calendar },
  { id: 'courts',    label: 'Courts & Fields', icon: MapPin },
  { id: 'schedule',  label: 'Schedule',        icon: CalendarDays },
  { id: 'gallery',   label: 'Gallery',         icon: Image },
  { id: 'contact',   label: 'Contact',         icon: Mail },
];

const Header = ({ onMenuClick, isMenuOpen }) => {
  const routeLocation = useRouteLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, themes, changeTheme, currentTheme } = useTheme();
  // Read-only — Header never calls connectChat() itself, so chatClient stays
  // null until something else (Messages page, squad Chat tab) connects it.
  const { chatClient } = useStreamChat();
  const { location: userLocation, permissionStatus, isLoading: locationLoading, requestLocation, clearLocation, isLocationEnabled } = useUserLocation();
  const { breadcrumbItems } = useBreadcrumb();
  const { liveEvents, notifications: pollNotifications, unreadMessages, markAllNotificationsRead, markNotificationRead } = usePoll();
  const { displayPublicFeeds: featureDisplayPublicFeeds, displayMarketplace: featureDisplayMarketplace, liveGameNotification: featureLiveGameNotification, academiesModule } = useFeatureFlags();
  // Global rollout toggle (exsports_core_api /features), not a per-user
  // entitlement — actual licensing enforcement happens server-side at
  // academy-creation time, not here.
  const showAcademies = academiesModule;
  const notifications = pollNotifications.items;
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [extraNotifications, setExtraNotifications] = useState([]);
  const [notifOffset, setNotifOffset] = useState(10);
  const [loadingMoreNotifs, setLoadingMoreNotifs] = useState(false);
  const notificationsLoading = false;
  const { imageUrl: profileImageUrl } = useProfileImage(user?.id, user?.profileImage);
  const [showReferModal, setShowReferModal] = useState(false);
  const [referEmail, setReferEmail] = useState('');
  const [referLoading, setReferLoading] = useState(false);
  const [referMessage, setReferMessage] = useState(null);
  const [referExistingUser, setReferExistingUser] = useState(null);
  // Only populated while a live Stream connection already exists (e.g. the
  // Messages page or a squad Chat tab is open this session) — gives instant
  // updates in that case instead of waiting on the ~10s poll tick.
  const [liveUnreadMessagesCount, setLiveUnreadMessagesCount] = useState(null);
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLiveEventsOpen, setIsLiveEventsOpen] = useState(false);
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
  const [isThemeSubmenuOpen, setIsThemeSubmenuOpen] = useState(false);
  const [pendingInvitationsCount, setPendingInvitationsCount] = useState(0);
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchModes, setSearchModes] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const searchDebounceRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const referModalRef = useRef(null);
  const userMenuRef = useRef(null);
  const liveEventsRef = useRef(null);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const navMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setIsNotificationDropdownOpen(false);
      }
      if (referModalRef.current && !referModalRef.current.contains(event.target)) {
        setShowReferModal(false);
        setReferEmail('');
        setReferMessage(null);
        setReferExistingUser(null);
      }
if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (liveEventsRef.current && !liveEventsRef.current.contains(event.target)) {
        setIsLiveEventsOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
      if (navMenuRef.current && !navMenuRef.current.contains(event.target)) {
        setIsNavMenuOpen(false);
      }
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target)) {
        setIsMobileSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounced search function
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      setSearchModes(null);
      return;
    }

    setIsSearching(true);
    try {
      // Pass user location for "near me" type searches
      const options = { limit: 5 };
      if (userLocation) {
        options.lat = userLocation.lat;
        options.lng = userLocation.lng;
      }
      const response = await searchAPI.search(query, options);
      setSearchResults(response.results);
      setSearchModes(response.searchModes);
      setIsSearchOpen(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
      setSearchModes(null);
    } finally {
      setIsSearching(false);
    }
  }, [userLocation]);

  // Handle search input change with debounce
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new debounce timer (300ms)
    searchDebounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  }, [performSearch]);

  // Handle search input focus
  const handleSearchFocus = () => {
    if (searchQuery.trim().length >= 2) {
      setIsSearchOpen(true);
    }
  };

  // Handle search result click
  const handleSearchResultClick = (item) => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults(null);

    // Navigate based on item type
    switch (item.type) {
      case 'user':
        navigate(`/player/${item.userHandle || item.id}`);
        break;
      case 'team':
        navigate(`/teams/${item.id}`);
        break;
      case 'facility':
        navigate(`/facilities/${item.id}`);
        break;
      case 'tournament':
        navigate(`/tournament/${item.id}`);
        break;
      case 'sport_type':
        navigate(`/sport/${item.name?.toLowerCase().replace(/\s+/g, '-')}`);
        break;
      case 'event':
        // Navigate to the event's owner (group or tournament)
        if (item.ownerType === 'group') {
          navigate(`/squads/${item.ownerId}?tab=current-event&eventId=${item.id}`);
        } else if (item.ownerType === 'tournament') {
          navigate(`/tournament/${item.ownerId}`);
        } else {
          navigate(`/my-games`);
        }
        break;
      case 'group':
        navigate(`/squads/${item.id}`);
        break;
      default:
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const fetchInvitationsCount = useCallback(() => {
    if (!isAuthenticated || document.visibilityState === 'hidden') return;
    invitationsApi.getMyInvitations()
      .then(res => setPendingInvitationsCount((res?.data || res || []).length))
      .catch(() => {});
  }, [isAuthenticated]);

  // Fetch pending invitations count — refresh on login and whenever navigating away from /invitations
  useEffect(() => {
    fetchInvitationsCount();
  }, [isAuthenticated, routeLocation.pathname, fetchInvitationsCount]);

  // Re-fetch when user returns from background so count is fresh
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchInvitationsCount();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, fetchInvitationsCount]);

  // Handle sending referral email
  const handleSendReferral = async () => {
    if (!referEmail.trim()) {
      setReferMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(referEmail)) {
      setReferMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setReferLoading(true);
    setReferMessage(null);

    try {
      const referrerName = user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name
        : 'A friend';

      const result = await apiCall('/invitations/refer', {
        method: 'POST',
        body: JSON.stringify({
          email: referEmail.trim(),
          referrerName
        })
      });

      if (result.userAlreadyExists) {
        setReferExistingUser(result.existingUser);
        setReferMessage({ type: 'info', text: 'This person is already on ExSports!' });
        return;
      }

      const messageText = result.isResend ? 'Referral resent successfully!' : 'Referral sent successfully!';
      setReferMessage({ type: 'success', text: messageText });
      setReferEmail('');

      // Close modal after 2 seconds on success
      setTimeout(() => {
        setShowReferModal(false);
        setReferMessage(null);
      }, 2000);
    } catch (error) {
      console.error('Referral error:', error);
      setReferMessage({ type: 'error', text: error.message || 'Failed to send referral' });
    } finally {
      setReferLoading(false);
    }
  };

  // Define which routes should show the back button
  const childPages = [
    '/signin', '/signup',
    '/basketball', '/volleyball', '/pickleball', '/scoring',
    '/product', '/checkout', '/marketplace/listing/new'
  ];

  const isChildPage = childPages.some(path =>
    routeLocation.pathname.startsWith(path) && routeLocation.pathname !== '/'
  );

  // Check if we're on the home page
  const isHomePage = routeLocation.pathname === '/' || routeLocation.pathname === '/home2';

  // Check if we're on an academy public page
  const isAcademyPage = routeLocation.pathname.startsWith('/academy/');
  const currentAcademyTab = new URLSearchParams(routeLocation.search).get('tab') || 'overview';

  // Get page title based on current route
  const getPageTitle = () => {
    const path = routeLocation.pathname;
    const pageMap = {
      '/signin': 'Sign In',
      '/signup': 'Sign Up',
      '/social-feed': 'Social Feed',
      '/profile': 'Profile',
      '/tournaments': 'Tournaments',
      '/basketball': 'Basketball',
      '/volleyball': 'Volleyball',
      '/pickleball': 'Pickleball',
      '/scoring': 'Live Scoring',

      '/checkout': 'Checkout',
      '/marketplace/listing/new': 'Sell Gear'
    };

    if (path.startsWith('/product/')) {
      return 'Product Details';
    }

    return pageMap[path] || 'Page';
  };

  const handleBackClick = () => {
    navigate(-1); // Go back in history
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  // Notifications and live events come from PollContext (polled every 10s).
  // Unread messages normally do too (no live Stream connection needed just to
  // show a badge) — but if a connection already exists because the user has
  // Messages or a squad Chat tab open, prefer live Stream events instead so
  // the badge updates instantly rather than waiting on the next poll tick.
  useEffect(() => {
    if (!chatClient || !isAuthenticated || !user?.id) {
      setLiveUnreadMessagesCount(null);
      return;
    }

    let debounceTimer = null;

    const fetchUnreadCount = async () => {
      try {
        const [dmChannels, groupChannels] = await Promise.all([
          chatClient.queryChannels(
            { type: 'messaging', members: { $in: [String(user.id)] } },
            [],
            { watch: false, state: true, limit: 50 }
          ),
          chatClient.queryChannels(
            { type: 'gaming', members: { $in: [String(user.id)] } },
            [],
            { watch: false, state: true, limit: 50 }
          ),
        ]);
        const dmUnread = dmChannels
          .filter(ch => ch.id?.startsWith('dm-'))
          .reduce((sum, ch) => sum + ch.countUnread(), 0);
        const groupUnread = groupChannels.reduce((sum, ch) => sum + ch.countUnread(), 0);
        setLiveUnreadMessagesCount(dmUnread + groupUnread);
      } catch (error) {
        console.error('Error fetching unread messages count:', error);
      }
    };

    const debouncedFetch = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(fetchUnreadCount, 2000);
    };

    fetchUnreadCount();

    // Increment locally on new message from someone else — no API call
    const handleNewMessage = (event) => {
      if (event.message?.user?.id !== String(user.id)) {
        const isDM = event.channel_id?.startsWith('dm-');
        const isGroup = event.channel_type === 'gaming';
        if (isDM || isGroup) {
          setLiveUnreadMessagesCount(prev => (prev ?? 0) + 1);
        }
      }
    };

    // Re-query after read, debounced so rapid reads collapse into one call
    const handleMarkRead = () => {
      debouncedFetch();
    };

    chatClient.on('message.new', handleNewMessage);
    chatClient.on('message.read', handleMarkRead);

    return () => {
      clearTimeout(debounceTimer);
      chatClient.off('message.new', handleNewMessage);
      chatClient.off('message.read', handleMarkRead);
    };
  }, [chatClient, isAuthenticated, user?.id]);


  // Handle live event click - navigate to the event
  const handleLiveEventClick = (event) => {
    setIsLiveEventsOpen(false);
    navigate(getLiveEventLink(event));
  };

  const unreadCount = pollNotifications.unread ?? notifications.filter(n => n.status === 'unread').length;
  // Prefer the live Stream-derived count when a connection already exists
  // (instant updates); otherwise fall back to the poll-derived count.
  const unreadMessagesCount = liveUnreadMessagesCount ?? unreadMessages;

  // Keep badge = unread notifications + unread messages whenever either count changes
  // or the app comes back to foreground (covers the case where Stream push arrived while backgrounded).
  useEffect(() => {
    setNativeBadge(unreadCount + unreadMessagesCount);
  }, [unreadCount, unreadMessagesCount]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setNativeBadge(unreadCount + unreadMessagesCount);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [unreadCount, unreadMessagesCount]);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if unread
      if (notification.status === 'unread') {
        markNotificationRead(notification.id);
        const newUnread = Math.max(0, (pollNotifications.unread ?? 0) - 1);
        setNativeBadge(newUnread + unreadMessagesCount);
        if (newUnread === 0 && unreadMessagesCount === 0) clearAndroidNotifications();
        notificationService.markAsRead(notification.id).catch(() => {});
      }

      // Navigate to the linked page if webLink exists (check both camelCase and snake_case)
      const link = notification.webLink || notification.web_link;
      if (link) {
        setIsNotificationDropdownOpen(false);

        // Special handling for tournament notifications to ensure they land on Tournament Info tab
        if (link.startsWith('/tournaments/')) {
          const tournamentId = link.split('/tournaments/')[1];
          navigate(`/tournament/${tournamentId}`, {
            state: { activeTab: 'details' }
          });
        } else {
          // Use window.location.href for full page navigation to ensure refresh
          // and proper handling of query params (e.g., ?tab=members)
          window.location.href = link;
        }
      }
    } catch (error) {
      console.error('Failed to update notification:', error);
    }
  };

  // Mark all notifications as read
  const handleMarkAllAsRead = () => {
    markAllNotificationsRead();
    setNativeBadge(unreadMessagesCount); // keep message badge, clear notification badge
    if (unreadMessagesCount === 0) clearAndroidNotifications();
    notificationService.markAllAsRead().catch(() => {});
  };

  // Load next page of notifications into the dropdown
  const handleLoadMoreNotifications = async () => {
    setLoadingMoreNotifs(true);
    try {
      const result = await notificationService.getNotifications({ limit: 10, offset: notifOffset });
      if (result.success) {
        const { items = [] } = result.data || {};
        setExtraNotifications(prev => [...prev, ...items]);
        setNotifOffset(prev => prev + 10);
      }
    } catch (err) {
      console.error('Failed to load more notifications:', err);
    }
    setLoadingMoreNotifs(false);
  };

  // Reset extra notifications when dropdown closes
  useEffect(() => {
    if (!isNotificationDropdownOpen) {
      setExtraNotifications([]);
      setNotifOffset(10);
    }
  }, [isNotificationDropdownOpen]);
  // Check if we're on auth pages (signin/signup)
  const isAuthPage = routeLocation.pathname.startsWith('/signin') || routeLocation.pathname.startsWith('/signup');

  return (
    <>
    <header className={`${theme.background} ${theme.shadow} sticky top-0 z-50 border-b ${theme.border}`} style={{ paddingTop: safeAreaTop('1rem'), paddingBottom: '1rem' }}>
      <div className={`flex justify-between items-center max-w-7xl mx-auto ${isAuthPage ? 'pr-4 lg:px-0' : 'px-4 lg:px-0'}`}>

        {/* Mobile full-width search — shown when isMobileSearchOpen */}
        {isMobileSearchOpen && isAuthenticated && (
          <div className="flex items-center gap-2 w-full md:hidden" ref={mobileSearchRef}>
            <button
              onClick={() => {
                setIsMobileSearchOpen(false);
                setSearchQuery('');
                setSearchResults(null);
                setIsSearchOpen(false);
              }}
              className={`p-2 rounded-lg ${theme.cardHover} flex-shrink-0`}
            >
              <ArrowLeft className={`w-5 h-5 ${theme.textSecondary}`} />
            </button>
            <div className="flex-1 relative" ref={searchRef}>
              <div className="relative w-full">
                {isSearching ? (
                  <Loader className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 animate-spin ${theme.textMuted}`} />
                ) : (
                  <Search className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.textMuted}`} />
                )}
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  placeholder="Search sports, events, players..."
                  autoFocus
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults(null);
                      setIsSearchOpen(false);
                    }}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full ${theme.cardHover}`}
                  >
                    <X className={`w-4 h-4 ${theme.textMuted}`} />
                  </button>
                )}
              </div>
              {isSearchOpen && (searchResults || isSearching) && (
                <SearchResults
                  results={searchResults}
                  isLoading={isSearching}
                  query={searchQuery}
                  onClose={() => { setIsSearchOpen(false); setIsMobileSearchOpen(false); }}
                  onResultClick={(result) => { handleSearchResultClick(result); setIsMobileSearchOpen(false); }}
                  searchModes={searchModes}
                  locationEnabled={isLocationEnabled}
                />
              )}
            </div>
          </div>
        )}

        <div className={`flex items-center gap-4 lg:gap-0 ${isMobileSearchOpen ? 'hidden md:flex' : ''}`}>
          {isChildPage ? (
            <button
              onClick={handleBackClick}
              className={`p-2 rounded-lg ${theme.cardHover} transition-colors`}
              title="Go back"
            >
              <ArrowLeft className={`w-6 h-6 ${theme.textSecondary}`} />
            </button>
          ) : (
            <div className="relative" ref={navMenuRef}>
              <button
                onClick={() => setIsNavMenuOpen(!isNavMenuOpen)}
                className={`lg:hidden p-2 rounded-lg ${theme.cardHover}`}
              >
                <Menu className={`w-6 h-6 ${theme.textSecondary}`} />
              </button>

              {/* Navigation Menu Dropdown */}
              {isNavMenuOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 bg-black/50 z-40"
                    onClick={() => setIsNavMenuOpen(false)}
                  />

                  {/* Dropdown Content */}
                  <div className={`fixed left-4 right-4 top-20 ${theme.card} rounded-xl shadow-xl z-50 overflow-hidden border ${theme.border}`}>
                    <div className="py-2">
                      {isAcademyPage ? (
                        ACADEMY_NAV_TABS.map(tab => {
                          const Icon = tab.icon;
                          return (
                            <button
                              key={tab.id}
                              onClick={() => { navigate(`${routeLocation.pathname}?tab=${tab.id}`); setIsNavMenuOpen(false); }}
                              className={`w-full px-4 py-3 text-left ${currentAcademyTab === tab.id ? theme.text : theme.textSecondary} ${theme.cardHover} transition-colors flex items-center gap-3 ${currentAcademyTab === tab.id ? 'font-semibold' : ''}`}
                            >
                              <Icon className="w-4 h-4" />
                              {tab.label}
                            </button>
                          );
                        })
                      ) : (
                      <>
                      <button
                        onClick={() => { navigate('/'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <Newspaper className="w-4 h-4" />
                        Home
                      </button>
                      {featureDisplayPublicFeeds && (
                      <button
                        onClick={() => { navigate('/social-feed'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <MessageSquare className="w-4 h-4" />
                        Feeds
                      </button>
                      )}
                      <button
                        onClick={() => { navigate('/teams'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <Users className="w-4 h-4" />
                        Teams
                      </button>
                      <button
                        onClick={() => { navigate('/tournaments'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <Trophy className="w-4 h-4" />
                        Tournaments
                      </button>
                      <button
                        onClick={() => { navigate('/squads'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <Users className="w-4 h-4" />
                        Squads
                      </button>
                      {showAcademies && (
                      <button
                        onClick={() => { navigate('/academies'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <GraduationCap className="w-4 h-4" />
                        Academies
                      </button>
                      )}
                      {featureDisplayMarketplace && (
                      <button
                        onClick={() => { navigate('/marketplace'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <Target className="w-4 h-4" />
                        Market Place
                      </button>
                      )}
                      <button
                        onClick={() => { navigate('/my-games'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <Trophy className="w-4 h-4" />
                        My Games
                      </button>
                      <button
                        onClick={() => { navigate('/predictions'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <Zap className="w-4 h-4" />
                        Predictions
                      </button>
                      <button
                        onClick={() => { navigate('/teammates'); setIsNavMenuOpen(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                      >
                        <UserCheck className="w-4 h-4" />
                        My Teammates
                      </button>
                      {pendingInvitationsCount > 0 && (
                        <button
                          onClick={() => { navigate('/invitations'); setIsNavMenuOpen(false); }}
                          className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                        >
                          <Mail className="w-4 h-4" />
                          <span className="flex-1">My Invitations</span>
                          <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {pendingInvitationsCount}
                          </span>
                        </button>
                      )}
                      </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          <Logo onClick={handleLogoClick} />
          {isChildPage && !routeLocation.pathname.startsWith('/signin') && !routeLocation.pathname.startsWith('/signup') && (
            <div className="hidden sm:block">
              <h1 className={`text-lg font-semibold ${theme.text}`}>{getPageTitle()}</h1>
            </div>
          )}
        </div>

        {/* Search Bar and Location Button - desktop only */}
        {isAuthenticated && (
        <div className="hidden md:flex items-center flex-1 max-w-md mx-8 gap-2">
          {/* Search Bar */}
          <div className="flex-1 relative" ref={searchRef}>
            <div className="relative w-full">
              {isSearching ? (
                <Loader className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 animate-spin ${theme.textMuted}`} />
              ) : (
                <Search className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.textMuted}`} />
              )}
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                placeholder="Search sports, events, players..."
                className={`w-full pl-10 pr-4 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults(null);
                    setIsSearchOpen(false);
                  }}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full ${theme.cardHover}`}
                >
                  <X className={`w-4 h-4 ${theme.textMuted}`} />
                </button>
              )}
            </div>
            {/* Search Results Dropdown */}
            {isSearchOpen && (searchResults || isSearching) && (
              <SearchResults
                results={searchResults}
                isLoading={isSearching}
                query={searchQuery}
                onClose={() => setIsSearchOpen(false)}
                onResultClick={handleSearchResultClick}
                searchModes={searchModes}
                locationEnabled={isLocationEnabled}
              />
            )}
          </div>

          {/* Location Button */}
          <button
            onClick={() => {
              if (isLocationEnabled) {
                clearLocation();
              } else {
                requestLocation();
              }
            }}
            disabled={locationLoading}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
              isLocationEnabled
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : permissionStatus === 'denied'
                ? 'bg-red-50 text-red-500 hover:bg-red-100'
                : `${theme.cardHover} ${theme.textSecondary}`
            }`}
            title={
              isLocationEnabled
                ? 'Location enabled - Click to disable'
                : permissionStatus === 'denied'
                ? 'Location denied - Click to retry'
                : 'Enable location for nearby searches'
            }
          >
            {locationLoading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : isLocationEnabled ? (
              <MapPin className="w-5 h-5" />
            ) : (
              <MapPinOff className="w-5 h-5" />
            )}
            <span className="text-xs hidden lg:inline">
              {isLocationEnabled ? 'Nearby' : 'Location'}
            </span>
          </button>
        </div>
        )}

        {/* User Actions */}
        <div className={`flex items-center gap-x-2 sm:gap-x-4 ${isMobileSearchOpen ? 'hidden md:flex' : ''}`}>
          {isAuthenticated ? (
            <>
              {/* Search icon - mobile only */}
              <button
                onClick={() => setIsMobileSearchOpen(true)}
                className={`md:hidden p-2 rounded-lg ${theme.cardHover}`}
                title="Search"
              >
                <Search className={`w-6 h-6 ${theme.textSecondary}`} />
              </button>

              {/* Live Events — desktop only; mobile surfaces this via the bottom nav's Live tab */}
              {featureLiveGameNotification && liveEvents.length > 0 && (
                <div className="relative hidden md:block" ref={liveEventsRef}>
                  <button
                    onClick={() => setIsLiveEventsOpen(!isLiveEventsOpen)}
                    className={`p-2 rounded-lg ${theme.cardHover} relative`}
                    title="Live Events"
                  >
                    <Radio className="w-6 h-6 text-red-500" />
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                      {liveEvents.length}
                    </span>
                  </button>

                  {/* Live Events Dropdown */}
                  {isLiveEventsOpen && (
                    <div className={`fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 top-16 sm:top-auto sm:mt-2 w-auto sm:w-96 ${theme.card} rounded-xl ${theme.shadow} border ${theme.border} z-50 overflow-hidden`}>
                      {/* Header */}
                      <div className="px-4 py-3 bg-red-600 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Radio className="w-4 h-4 text-white animate-pulse" />
                          <h3 className="font-semibold text-white text-sm">Live Now</h3>
                          <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {liveEvents.length}
                          </span>
                        </div>
                        <button
                          onClick={() => setIsLiveEventsOpen(false)}
                          className="p-1 rounded-lg hover:bg-white/20 transition-colors"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>

                      <LiveEventsList liveEvents={liveEvents} onEventClick={handleLiveEventClick} />
                    </div>
                  )}
                </div>
              )}

              {/* Messages */}
              <button
                onClick={() => navigate('/messages')}
                className={`p-2 rounded-lg ${theme.cardHover} relative`}
                title="Messages"
              >
                <MessageCircle className={`w-6 h-6 ${theme.textSecondary}`} />
                {unreadMessagesCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                  </span>
                )}
              </button>

              {/* Notifications */}
              <div className="relative" ref={notificationDropdownRef}>
                <button
                  onClick={() => setIsNotificationDropdownOpen(!isNotificationDropdownOpen)}
                  className={`p-2 rounded-lg ${theme.cardHover} relative`}
                  title="Notifications"
                >
                  <Bell className={`w-6 h-6 ${theme.textSecondary}`} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {isNotificationDropdownOpen && (
                  <div className={`fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 top-16 sm:top-auto sm:mt-2 sm:w-96 ${theme.card} rounded-lg ${theme.shadow} border ${theme.border} z-50 overflow-hidden`}>
                    {/* Header */}
                    <div className={`px-4 py-3 border-b ${theme.border} flex items-center justify-between`}>
                      <h3 className={`font-semibold ${theme.text}`}>Notifications</h3>
                      <div className="flex items-center gap-x-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={handleMarkAllAsRead}
                            className={`text-xs ${theme.primary} hover:underline`}
                          >
                            Mark all as read
                          </button>
                        )}
                        <button
                          onClick={() => setIsNotificationDropdownOpen(false)}
                          className={`p-1 rounded ${theme.cardHover}`}
                        >
                          <X className={`w-4 h-4 ${theme.textMuted}`} />
                        </button>
                      </div>
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-80 overflow-y-auto">
                      {notificationsLoading ? (
                        <div className="p-4 text-center">
                          <div className={`text-sm ${theme.textMuted}`}>Loading notifications...</div>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="p-4 text-center">
                          <div className={`text-sm ${theme.textMuted}`}>No notifications yet</div>
                        </div>
                      ) : (
                        [...notifications, ...extraNotifications]
                          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                          .map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() => handleNotificationClick(notification)}
                              className={`px-4 py-3 border-b ${theme.border} cursor-pointer transition-colors ${theme.cardHover} ${
                                notification.status === 'unread'
                                  ? `${theme.backgroundSecondary}`
                                  : ''
                              }`}
                            >
                              <div className="flex items-start gap-x-3">
                                <div className={`mt-1 w-2 h-2 rounded-full ${
                                  notification.status === 'unread' ? 'bg-blue-500' : 'bg-gray-300'
                                }`}></div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${theme.text} leading-5`}>
                                    {notification.message}
                                  </p>
                                  <p className={`text-xs ${theme.textMuted} mt-1`}>
                                    {new Date(notification.createdAt).toLocaleString()}
                                  </p>
                                </div>
                                {notification.status === 'unread' && (
                                  <div className="mt-1">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                      )}
                    </div>

                    {/* Footer */}
                    {(() => {
                      const displayed = notifications.length + extraNotifications.length;
                      const pollTotal = pollNotifications.total;
                      // Show "load more" if API told us total, or heuristically if we got a full page (≥10)
                      const hasMore = pollTotal != null ? displayed < pollTotal : notifications.length >= 10;
                      return (displayed > 0 || hasMore) && (
                        <div className={`px-4 py-3 border-t ${theme.border} flex items-center justify-between gap-2`}>
                          {hasMore ? (
                            <button
                              onClick={handleLoadMoreNotifications}
                              disabled={loadingMoreNotifs}
                              className="flex items-center gap-1.5 text-sm text-blue-500 hover:underline disabled:opacity-50"
                            >
                              {loadingMoreNotifs
                                ? <><Loader className="w-3 h-3 animate-spin" /> Loading...</>
                                : pollTotal != null
                                  ? `Load next ${Math.min(10, pollTotal - displayed)}`
                                  : 'Load more'
                              }
                            </button>
                          ) : <span />}
                          <button
                            className={`text-sm ${theme.primary} hover:underline`}
                            onClick={() => {
                              navigate('/notifications');
                              setIsNotificationDropdownOpen(false);
                            }}
                          >
                            View all
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                {/* Named, because its only content is an avatar: with a picture
                    the button was announced as the user's name, and without one
                    as a single letter. Neither says what it opens. */}
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-label="Profile menu"
                  aria-expanded={isUserMenuOpen}
                  className={`flex items-center gap-x-2 p-2 rounded-lg ${theme.cardHover}`}
                >
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl}
                      alt={user.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-white">
                        {user?.firstName?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                </button>

                {/* User Dropdown menu */}
                {isUserMenuOpen && (
                  <div className={`absolute right-0 mt-2 w-56 ${theme.card} rounded-lg ${theme.shadow} border ${theme.border} z-50`}>
                    <div className="py-1">
                      {/* User Info */}
                      <div className={`px-4 py-3 border-b ${theme.border}`}>
                        <p className={`text-sm font-medium ${theme.text}`}>{user.name}</p>
                        <p className={`text-xs ${theme.textMuted}`}>{user.email}</p>
                      </div>

                      {/* Profile */}
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setIsUserMenuOpen(false);
                        }}
                        className={`flex items-center w-full px-4 py-3 text-sm ${theme.textSecondary} ${theme.cardHover}`}
                      >
                        <User className="w-5 h-5 mr-3" />
                        Profile
                      </button>

                      {/* Refer a Friend */}
                      <button
                        onClick={() => {
                          setShowReferModal(true);
                          setIsUserMenuOpen(false);
                        }}
                        className={`flex items-center w-full px-4 py-3 text-sm ${theme.textSecondary} ${theme.cardHover}`}
                      >
                        <UserPlus className="w-5 h-5 mr-3" />
                        Refer a Friend
                      </button>

                      {/* Kyna */}
                      <button
                        onClick={() => {
                          window.dispatchEvent(new Event('show-help-chat'));
                          setIsUserMenuOpen(false);
                        }}
                        className={`flex items-center w-full px-4 py-3 text-sm ${theme.textSecondary} ${theme.cardHover}`}
                      >
                        <MessageCircle className="w-5 h-5 mr-3" />
                        Kyna
                      </button>

                      {/* Divider */}
                      <div className={`my-1 border-t ${theme.border}`}></div>

                      {/* Themes Submenu */}
                      <button
                        onClick={() => setIsThemeSubmenuOpen(!isThemeSubmenuOpen)}
                        className={`flex items-center w-full px-4 py-3 text-sm ${theme.textSecondary} ${theme.cardHover} justify-between`}
                      >
                        <span className="flex items-center">
                          <div className="w-5 h-5 rounded-full mr-3 bg-gradient-to-r from-blue-500 to-purple-600"></div>
                          Themes
                        </span>
                        <ChevronRight className={`w-4 h-4 transition-transform ${isThemeSubmenuOpen ? 'rotate-90' : ''}`} />
                      </button>

                      {/* Theme Options Submenu */}
                      {isThemeSubmenuOpen && (
                        <div className={`border-t ${theme.border} bg-opacity-50 ${theme.backgroundSecondary}`}>
                          {themes.map((themeOption) => (
                            <button
                              key={themeOption.key}
                              onClick={() => {
                                changeTheme(themeOption.key);
                                setIsUserMenuOpen(false);
                                setIsThemeSubmenuOpen(false);
                              }}
                              className={`flex items-center w-full px-8 py-2 text-sm ${theme.cardHover} ${
                                currentTheme === themeOption.key
                                  ? `${theme.primary.includes('bg-blue') ? 'bg-blue-50 text-blue-600' : theme.primary}`
                                  : theme.textSecondary
                              }`}
                            >
                              <div className="w-3 h-3 rounded-full mr-3" style={{
                                background: themeOption.key === 'light' ? '#3b82f6' :
                                          themeOption.key === 'dark' ? '#1f2937' :
                                          themeOption.key === 'sports' ? '#16a34a' :
                                          themeOption.key === 'ocean' ? '#0891b2' :
                                          themeOption.key === 'sunset' ? '#ea580c' :
                                          themeOption.key === 'purple' ? '#9333ea' :
                                          themeOption.key === 'darkGreen' ? '#059669' :
                                          themeOption.key === 'darkBlue' ? '#2563eb' :
                                          themeOption.key === 'darkPurple' ? '#9333ea' :
                                          themeOption.key === 'darkOrange' ? '#ea580c' :
                                          '#3b82f6'
                              }}></div>
                              {themeOption.name}
                              {currentTheme === themeOption.key && (
                                <span className="ml-auto text-blue-600">✓</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Divider */}
                      <div className={`my-1 border-t ${theme.border}`}></div>

                      {/* Sign Out */}
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsUserMenuOpen(false);
                        }}
                        className={`flex items-center w-full px-4 py-3 text-sm ${theme.textSecondary} ${theme.cardHover}`}
                      >
                        <LogIn className="w-5 h-5 mr-3" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Refer Modal */}
              {showReferModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div ref={referModalRef} className={`${theme.card} rounded-lg ${theme.shadow} w-full max-w-md mx-4 p-6`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-lg font-semibold ${theme.text}`}>Refer a Friend</h3>
                      <button
                        onClick={() => {
                          setShowReferModal(false);
                          setReferEmail('');
                          setReferMessage(null);
                          setReferExistingUser(null);
                        }}
                        className={`p-1 rounded ${theme.cardHover}`}
                      >
                        <X className={`w-5 h-5 ${theme.textMuted}`} />
                      </button>
                    </div>

                    <p className={`text-sm ${theme.textMuted} mb-4`}>
                      Invite your friends to join Kynaix Sports! Enter their email address below.
                    </p>

                    {referMessage && (
                      <div className={`mb-4 p-3 rounded-lg text-sm ${
                        referMessage.type === 'success'
                          ? 'bg-green-50 text-green-800 border border-green-200'
                          : referMessage.type === 'info'
                          ? 'bg-blue-50 text-blue-800 border border-blue-200'
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}>
                        {referMessage.text}
                        {referExistingUser && (
                          <button
                            onClick={() => {
                              setShowReferModal(false);
                              setReferEmail('');
                              setReferMessage(null);
                              setReferExistingUser(null);
                              navigate(`/player/${referExistingUser.userHandle}`);
                            }}
                            className={`mt-2 block w-full text-center ${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
                          >
                            View {referExistingUser.firstName}'s Profile
                          </button>
                        )}
                      </div>
                    )}

                    <div className="mb-4">
                      <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.textMuted}`} />
                        <input
                          type="email"
                          value={referEmail}
                          onChange={(e) => setReferEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSendReferral()}
                          placeholder="friend@example.com"
                          className={`w-full pl-10 pr-4 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                          disabled={referLoading}
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleSendReferral}
                      disabled={referLoading}
                      className={`w-full flex items-center justify-center ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
                    >
                      {referLoading ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Send Invitation
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <GuestMenuDropdown />
          )}
        </div>
      </div>
    </header>

    {/* Secondary Navigation Bar - Desktop Only */}
    {(isAuthenticated || isAcademyPage) && !isChildPage && (
      <nav className={`hidden lg:block ${theme.background} border-b ${theme.border} sticky top-[73px] z-40`}>
        <div className="max-w-7xl mx-auto px-0">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            {isAcademyPage ? (
              ACADEMY_NAV_TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => navigate(`${routeLocation.pathname}?tab=${tab.id}`)}
                    className={`${theme.tabBase} whitespace-nowrap flex items-center gap-1.5 ${
                      currentAcademyTab === tab.id ? theme.tabActive : theme.tabInactive
                    }`}
                  >
                    <Icon className="w-4 h-4" />{tab.label}
                  </button>
                );
              })
            ) : (
              <>
            <button
              onClick={() => navigate('/')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname === '/' || routeLocation.pathname === '/home2'
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              Home
            </button>
            {featureDisplayPublicFeeds && (
            <button
              onClick={() => navigate('/social-feed')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname.startsWith('/social-feed')
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              Feeds
            </button>
            )}
            <button
              onClick={() => navigate('/teams')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname.startsWith('/teams')
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              Teams
            </button>
            <button
              onClick={() => navigate('/tournaments')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname.startsWith('/tournaments')
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              Tournaments
            </button>
            <button
              onClick={() => navigate('/squads')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname.startsWith('/squads')
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              Squads
            </button>
            {showAcademies && (
            <button
              onClick={() => navigate('/academies')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname.startsWith('/academies')
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              Academies
            </button>
            )}
            {featureDisplayMarketplace && (
            <button
              onClick={() => navigate('/marketplace')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname.startsWith('/marketplace')
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              Market Place
            </button>
            )}
            <button
              onClick={() => navigate('/my-games')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname.startsWith('/my-games')
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              My Games
            </button>
            <button
              onClick={() => navigate('/predictions')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname.startsWith('/predictions')
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              Predictions
            </button>
            <button
              onClick={() => navigate('/teammates')}
              className={`${theme.tabBase} whitespace-nowrap ${
                routeLocation.pathname.startsWith('/teammates')
                  ? theme.tabActive
                  : theme.tabInactive
              }`}
            >
              My Teammates
            </button>
            {pendingInvitationsCount > 0 && (
              <button
                onClick={() => navigate('/invitations')}
                className={`${theme.tabBase} whitespace-nowrap relative ${
                  routeLocation.pathname.startsWith('/invitations')
                    ? theme.tabActive
                    : theme.tabInactive
                }`}
              >
                My Invitations
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {pendingInvitationsCount}
                </span>
              </button>
            )}
              </>
            )}
          </div>
        </div>
      </nav>
    )}

    {/* Breadcrumb Bar - Below navigation tabs */}
    {breadcrumbItems && breadcrumbItems.length > 0 && (
      <div className={`${theme.background}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-0 py-2">
          <nav className="flex items-center gap-x-2 text-sm">
            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <ChevronRight className={`w-4 h-4 ${theme.textMuted}`} />
                )}
                {item.path ? (
                  <button
                    onClick={() => navigate(item.path)}
                    className={`${theme.textSecondary} hover:${theme.text} transition-colors`}
                  >
                    {item.label}
                  </button>
                ) : (
                  <span className={theme.text}>{item.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
      </div>
    )}
    </>
  );
};

export default Header;